import './BetradarScoreboardCustomized.less';

import React from 'react';
import { useReframe } from 'nike-re-framejs';
import clsx from 'clsx';
import moment from 'moment';
import { fromJS, Map } from 'immutable';
import BetradarMTScoreboard from '../../betradar-widgets/BetradarMTScoreboard';
import { match$$ } from '../../turboLive/push/overview/subs';
import { localize } from '../../turboLive/utils';
import { MatchTimer } from '../../turboLive/components/MatchTimer';
import SportProvider from '../../turboLive/components/SportProvider';
import { isLiveEvent$$ } from '../../match-statistics/subs';
import { SvgIcon } from '../../../icons/SvgIcon';
import { isAmericanFootbal, isFootball, isHockey, isMotorizmus, isUFC } from '../../match-statistics/utils';
import i18n from '../../../common/i18n';
import { useIsBetradarWidgetAvailable } from '../../match-statistics/hooks/useIsBetradarWidgetAvailable';
import { getImgId, sportsWithBRTimer, sportWithCustomOpponents } from './utils';
import {
    BetradarScoreboardCardsType,
    BetradarScoreboardCustomizedType,
    BetradarScoreboardPenaltiesType,
    OpponentsScoreType,
    SingleOpponentScoreboardType
} from './types';
import { getScoreboardType } from '../../turboLive/matchExtras';
import { isMobileChannel } from '../../../common/util';

function getServeIcon(sportId, mobile) {
    if (sportId === 61) {
        return <div className="betradar-scoreboard-customized-basic-serve-icon mx-3" />;
    }
    //pre rovnaku ikonu v scoreboarde pre plazovy a klasicky volejbal - toto sa zrejme este rozsiri v buducnosti pre ine sporty
    return (
        <SvgIcon
            imgId={getImgId(sportId)}
            sizeX="10px"
            sizeY="10px"
            title="server-icon"
            className={`${mobile ? 'c-white' : 'c-black-100'} mx-3 flex-shrink-0`}
        />
    );
}

//children are specificaly used only for old live "timer stats", it would be too much hassle to try to rewrite it from mobile/portal components, so delete it in 4 months?
function BetradarScoreboardCustomized({
    sportId,
    streamId,
    sportCode,
    eventId,
    match,
    mobile = false
}: BetradarScoreboardCustomizedType) {
    const { subscribe } = useReframe();
    const isBrAvaiable = useIsBetradarWidgetAvailable(match);
    const betradarWidgetsAvailable = streamId && isBrAvaiable && !isUFC(sportId);
    const isLiveEvent = subscribe(isLiveEvent$$);

    // TLV data
    const matchTLV = subscribe(match$$(sportCode, eventId));
    const tlvScore = matchTLV?.score;
    const tlvTotalScore = tlvScore?.scores?.TOTAL;
    const gameTotalScore = tlvScore?.scores?.GAME;
    const periodScores = tlvScore?.periodScores;
    const tlvStats = matchTLV?.stats;
    const penalties = tlvStats?.SUSPENDS;
    const corners = tlvStats?.CORNERS;

    // calculations
    const showCornersAndCards = isLiveEvent && isFootball(sportId);
    const showPenalties = isLiveEvent && isHockey(sportId);
    const score = gameTotalScore || tlvTotalScore;
    const prematchOpponents = match?.get('participants') || match?.participants;
    const opponentHome =
        !isLiveEvent && prematchOpponents
            ? prematchOpponents.get(0) || prematchOpponents[0]
            : prematchOpponents?.home
            ? prematchOpponents?.home
            : localize(matchTLV?.home);
    const opponentAway =
        !isLiveEvent && prematchOpponents
            ? prematchOpponents.get(1) || prematchOpponents[1]
            : prematchOpponents?.away
            ? prematchOpponents?.away
            : localize(matchTLV?.away);
    const result = match?.result || match?.get('result');
    const serve = tlvScore?.server;
    const currentPeriod = localize(matchTLV?.timer?.currentPeriod) || (result && i18n('nui.turbo.live.finishedEvent'));
    const singleOpponent = !betradarWidgetsAvailable && !opponentAway;
    const matchExpiration = match?.expiration || match?.get('expiration');
    // full custom scoreboard if - match is live && (BR widget is not available || match has no MT) || match is prematch but no widget is available
    const isFullCustomScoreboard =
        isUFC(sportId) ||
        (isLiveEvent && (!betradarWidgetsAvailable || !streamId)) ||
        (!isLiveEvent && !betradarWidgetsAvailable);
    if (!eventId) {
        return null;
    }

    if (singleOpponent) {
        const scoreboardData = isLiveEvent ? currentPeriod : matchExpiration;
        return (
            <SingleOpponentScoreboard
                opponent={opponentHome}
                mobile={mobile}
                isLiveEvent={isLiveEvent}
                scoreboardData={scoreboardData}
            />
        );
    }
    const customOpponents = sportWithCustomOpponents(sportId);
    const showInfoRow = showCornersAndCards || !showPenalties;
    const Periods = () => <PeriodScores periodScores={periodScores} sportId={sportId} mobile={mobile} />;
    return (
        <div
            className={clsx('p-rel betradar-scoreboard', {
                'my-5 pt-10': !(isAmericanFootbal(sportId) && !isLiveEvent) // dont use when prematch and nfl
            })}
            data-atid="betradar-scoreboard"
        >
            {/* - if score is available from our sources - show our score, period score and other bonus info - for live matches
                - if match is prematch - show date and time of match start
                - if betradar widgets are available for this match - then this time is from BR sources
            */}
            {(isLiveEvent && opponentHome) || !isLiveEvent ? (
                <div
                    className={clsx('absolute-center flex flex-col', {
                        'c-white': mobile,
                        'justify-between': !result
                    })}
                >
                    <CustomTimer
                        sportCode={sportCode}
                        mobile={mobile}
                        matchTLV={matchTLV}
                        sportId={sportId}
                        result={result && !betradarWidgetsAvailable}
                    />
                    <OpponentsAndScore
                        opponentAway={opponentAway}
                        opponentHome={opponentHome}
                        isLiveEvent={isLiveEvent}
                        sportId={sportId}
                        serveIcon={getServeIcon(sportId, mobile)}
                        betradarWidgetsAvailable={betradarWidgetsAvailable}
                        isFullCustomScoreboard={isFullCustomScoreboard}
                        score={score}
                        matchExpiration={matchExpiration}
                        result={result}
                        serve={serve}
                        customOpponents={customOpponents}
                    />
                    {showInfoRow && (
                        <div className="full-width flex justify-between">
                            <div
                                className={clsx(
                                    'betradar-scoreboard-customized-cards-inner full-width flex justify-between px-15',
                                    {
                                        'justify-center': sportId !== 1
                                    }
                                )}
                            >
                                {showCornersAndCards && <BetradarScoreboardCards indicator={'home'} stats={tlvStats} />}
                                <div>
                                    {!showPenalties && <Periods />}
                                    {showCornersAndCards && (
                                        <BetradarScoreboardBonusInfo>
                                            {tlvStats ? corners.split(':')[0] : corners?.home ? corners?.home : 0}
                                            <SvgIcon
                                                imgId="corner-kick"
                                                sizeX="12px"
                                                sizeY="12px"
                                                title="corner-kick-icon"
                                                className={`${mobile ? 'c-white' : 'c-black-100'} mx-5`}
                                            />
                                            {tlvStats ? corners.split(':')[1] : corners?.away ? corners?.away : 0}
                                        </BetradarScoreboardBonusInfo>
                                    )}
                                </div>

                                {showCornersAndCards && <BetradarScoreboardCards indicator={'away'} stats={tlvStats} />}
                            </div>
                        </div>
                    )}
                    {showPenalties && (
                        <div className={'flex justify-between px-15'}>
                            <BetradarScoreboardPenalties penalties={penalties} indicator={'home'} stats={tlvStats} />
                            <Periods />
                            <BetradarScoreboardPenalties penalties={penalties} indicator={'away'} stats={tlvStats} />
                        </div>
                    )}
                </div>
            ) : null}

            {!isFullCustomScoreboard && betradarWidgetsAvailable && !customOpponents ? (
                <BetradarMTScoreboard matchId={streamId} sportId={sportId} hideBetradarData={isLiveEvent} />
            ) : (
                <div
                    className={
                        isLiveEvent && (sportId === 1 || sportId === 3)
                            ? 'betradar-scoreboard-customized-holder-expanded'
                            : 'betradar-scoreboard-customized-holder mt-15'
                    }
                />
            )}
        </div>
    );
}

function PeriodScores({ sportId, periodScores, mobile }: { sportId: number; periodScores: any; mobile?: boolean }) {
    return (
        <BetradarScoreboardBonusInfo
            fullWidth
            className={clsx('period-score', { 'betradar-scoreboard-customized-shadow': mobile })}
        >
            {(sportId === 85 || sportId === 88 || sportId === 59) && periodScores?.length > 1 ? (
                <div className="flex">
                    <div>
                        {i18n(
                            'nui.betradar.snooker.lastPeriod',
                            periodScores[periodScores.length - 2].score.home,
                            periodScores[periodScores.length - 2].score.away
                        )}
                    </div>
                    <div className="pl-10 text-extra-bold">
                        {i18n(
                            'nui.betradar.snooker.scoreNow',
                            periodScores[periodScores.length - 1].score.home,
                            periodScores[periodScores.length - 1].score.away
                        )}
                    </div>
                </div>
            ) : (
                <PeriodScoresWrap periodScores={periodScores} />
            )}
        </BetradarScoreboardBonusInfo>
    );
}

const PeriodScoresWrap = ({ periodScores }) => {
    if (!periodScores?.length) {
        return null;
    }
    const hasHalfTimeIndex = periodScores.findIndex(period => period.periodType === 'HALFTIME_SCORE');
    if (hasHalfTimeIndex > 0) {
        const firstHalf = periodScores.slice(0, hasHalfTimeIndex);
        const secondHalf = periodScores.slice(hasHalfTimeIndex + 1);
        const halfTime = periodScores.slice(hasHalfTimeIndex, hasHalfTimeIndex + 1);
        return (
            <div className="flex">
                <div className="flex-1 flex justify-end items-center">
                    <PeriodScoresView periodScores={firstHalf} />
                </div>
                <PeriodScoresView periodScores={halfTime} />
                <div className="flex-1 flex justify-start items-center">
                    <PeriodScoresView periodScores={secondHalf} />
                </div>
            </div>
        );
    }
    return <PeriodScoresView periodScores={periodScores} />;
};

const PeriodScoresView = ({ periodScores }) =>
    periodScores.map((period, index) => (
        <div
            className={clsx('mx-3', {
                'br-default br-radius-xxs px-2': period.periodType === 'HALFTIME_SCORE',
                'text-extra-bold': index === periodScores.length - 1
            })}
            key={period?.period?.code || index}
        >
            {period?.score?.home}:{period?.score?.away}
        </div>
    ));

const MatchExpiration = ({ expiration }) =>
    expiration ? (
        <div className="full-width text-center">
            <div className={clsx('betradar-match-expiration fs-12', { 'betradar-opacity': isMobileChannel() })}>
                {moment(expiration).format('MMMM DD')}
            </div>
            <div>{moment(expiration).format('HH:mm')}</div>
        </div>
    ) : null;

function OpponentsAndScore({
    isFullCustomScoreboard,
    sportId,
    betradarWidgetsAvailable,
    opponentHome,
    opponentAway,
    result,
    isLiveEvent,
    serve,
    serveIcon,
    matchExpiration,
    score,
    customOpponents
}: OpponentsScoreType) {
    const scoreHome = score?.home === '00' ? 0 : score?.home || result?.homeScore || result?.get('homeScore') || 0;
    const scoreAway = score?.away === '00' ? 0 : score?.away || result?.awayScore || result?.get('awayScore') || 0;
    const showOpponents = (isFullCustomScoreboard && !betradarWidgetsAvailable) || customOpponents;

    const formatOpponents = opponet => {
        const splitOponnet = opponet.split('/');
        return splitOponnet.length > 1 ? (
            <div className="flex flex-col fs-12">
                <span className="flex-1 ellipsis pb-5">{splitOponnet[0]}</span>
                <span className="flex-1 ellipsis">{splitOponnet[1]}</span>
            </div>
        ) : (
            opponet
        );
    };

    return (
        <div
            className={clsx('fs-21 items-center full-width flex justify-center', {
                'lh-1': isAmericanFootbal(sportId)
            })}
        >
            {showOpponents && (
                <div className="ellipsis fs-14 full-width pl-15 pr-5" title={opponentHome} data-atid="detail-home-team">
                    {formatOpponents(opponentHome)}
                </div>
            )}
            {!isLiveEvent && !betradarWidgetsAvailable && !result ? (
                <MatchExpiration expiration={matchExpiration} />
            ) : (isLiveEvent && !isMotorizmus(sportId)) || (!betradarWidgetsAvailable && result) ? (
                <div className="p-rel flex items-center full-width">
                    <div className="flex flex-1 justify-end items-center">
                        {serve === 'HOME' && serveIcon}
                        {scoreHome}
                    </div>

                    <span className="mx-5">:</span>

                    <div className="flex flex-1 justify-start items-center">
                        {scoreAway}
                        {serve === 'AWAY' && serveIcon}
                    </div>
                </div>
            ) : null}

            {showOpponents && (
                <div
                    className="ellipsis fs-14 full-width text-right pl-5 pr-15"
                    title={opponentAway}
                    data-atid="detail-away-team"
                >
                    {formatOpponents(opponentAway)}
                </div>
            )}
        </div>
    );
}

function CustomTimer({ matchTLV, sportCode, result, mobile, sportId }) {
    const brTimer = sportsWithBRTimer(sportId);
    return (
        <div className={clsx('fs-10 flex justify-center', { 'my-5 ': brTimer })}>
            {!brTimer ? (
                <div
                    className={clsx('px-3 c-black-50', {
                        'pt-20': result,
                        'c-white betradar-opacity': result && isMobileChannel()
                    })}
                >
                    {!!matchTLV ? (
                        <SportProvider sportName={sportCode}>
                            <MatchTimer
                                scoreboardTypeId={getScoreboardType(fromJS(matchTLV) as Map<string, any>)}
                                matchTimer={fromJS(matchTLV.timer)}
                                otherClass={`fs-12 ${mobile ? 'betradar-opacity' : 'c-black-50'} ${
                                    isAmericanFootbal(sportId) && mobile ? 'lh-1-5' : ''
                                }`}
                            />
                        </SportProvider>
                    ) : result ? (
                        i18n('nui.match.finished')
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function SingleOpponentScoreboard({ opponent, mobile, isLiveEvent, scoreboardData }: SingleOpponentScoreboardType) {
    return (
        <div className={clsx('betradar-scoreboard-customized-holder p-rel mt-15', { 'c-white': mobile })}>
            {isLiveEvent ? (
                <div className={clsx('fs-10 flex justify-center', { 'c-black-50': !mobile })}>
                    <div className="px-3">{scoreboardData}</div>
                </div>
            ) : (
                <MatchExpiration expiration={scoreboardData} />
            )}

            <div
                className={`text-bold px-15 text-center pt-10 ${mobile ? 'fs-12' : 'fs-14'}`}
                data-atid="detail-home-team"
            >
                {opponent}
            </div>
        </div>
    );
}

function BetradarScoreboardBonusInfo({
    children,
    className,
    fullWidth
}: {
    children: any;
    className?: string;
    fullWidth?: boolean;
}) {
    return (
        <div
            className={clsx(
                'betradar-scoreboard-customized-bonus-info fs-10 flex flex-wrap justify-center items-center',
                { 'betradar-scoreboard-customized-bonus-mw': !fullWidth },
                className
            )}
        >
            {children}
        </div>
    );
}

function BetradarScoreboardCards({ indicator, stats }: BetradarScoreboardCardsType) {
    // double source of data = double trouble
    const yellow = stats && stats.YELLOW_CARDS?.split(':')[indicator === 'home' ? 0 : 1];
    const red = stats && stats.RED_CARDS?.split(':')[indicator === 'home' ? 0 : 1];
    return (
        <BetradarScoreboardBonusInfo>
            <div
                className={clsx('betradar-scoreboard-customized-cards flex items-center', {
                    'betradar-scoreboard-customized-grayed': !parseInt(yellow)
                })}
            >
                <div className="betradar-scoreboard-customized-card-icon mr-5" />
                {yellow || 0}
            </div>

            <div
                className={clsx('betradar-scoreboard-customized-cards flex items-center', {
                    'betradar-scoreboard-customized-grayed': !parseInt(red)
                })}
            >
                <div className="betradar-scoreboard-customized-card-icon ml-5 mr-5 betradar-scoreboard-customized-card-icon-red" />
                {red || 0}
            </div>
        </BetradarScoreboardBonusInfo>
    );
}

function BetradarScoreboardPenalties({ penalties, indicator, stats }: BetradarScoreboardPenaltiesType) {
    const tlvPenalties = stats && stats.SUSPENDS?.split(':')[indicator === 'home' ? 0 : 1];
    const data = stats ? parseInt(tlvPenalties) : parseInt(penalties?.[indicator]);
    const fill = data ? 'c-red' : 'c-black-40';
    return (
        <BetradarScoreboardBonusInfo>
            <div
                title={i18n('nui.turbo.live.match.penalties')}
                className={clsx('flex items-center', { 'betradar-scoreboard-customized-grayed': !data })}
            >
                <SvgIcon className={fill} imgId="penalty-whistle" sizeX="14px" sizeY="14px" />
                <span className={'mx-3'}> {data || 0} </span>
            </div>
        </BetradarScoreboardBonusInfo>
    );
}

export default BetradarScoreboardCustomized;
